import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useLightSwitchState } from '../../../../../../hooks/court/useLightSwitchState'
import { useUpdateLightSwitchState } from '../../../../../../hooks/court/useUpdateLightSwitchState'

import { FloodlightSvg } from '../../../../../ui/floodlightSvg/FloodlightSvg'

import { useApp } from '../../../../../../contexts/AppContext'
import { BannerKind } from '../../../../../../types/contexts/AppContext'

type CourtNightLightingProps = {
  lightSwitchName: string
}

export const CourtNightLighting = ({
  lightSwitchName,
}: CourtNightLightingProps) => {
  const { t } = useTranslation('courts')
  const { showBanner } = useApp()
  const {
    state,
    loading: stateLoading,
    error: stateError,
    getState,
    resetError: resetStateError,
  } = useLightSwitchState(lightSwitchName)
  const {
    updateState,
    loading: updateLoading,
    error: updateError,
    resetError: resetUpdateError,
  } = useUpdateLightSwitchState()

  const nightLightingEnabled = state?.enabled ?? false
  const loading = stateLoading || updateLoading
  const error = stateError || updateError

  useEffect(() => {
    if (!error) {
      return
    }

    showBanner({ kind: BannerKind.ERR, msg: error })
    resetStateError()
    resetUpdateError()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, showBanner])

  const toggleNightLighting = async () => {
    const nextValue = !nightLightingEnabled
    const updatedState = await updateState(lightSwitchName, nextValue)

    if (!updatedState) {
      return
    }

    await getState()
    showBanner({
      kind: BannerKind.OK,
      msg: updatedState.enabled
        ? t('courts.card.nocturna.enabled')
        : t('courts.card.nocturna.disabled'),
    })
  }

  return (
    <button
      type="button"
      className="court-night-lighting"
      onClick={() => void toggleNightLighting()}
      disabled={loading || !state?.reachable}
      aria-pressed={nightLightingEnabled}
      aria-label={
        nightLightingEnabled
          ? t('courts.card.nocturna.off')
          : t('courts.card.nocturna.on')
      }
      title={
        nightLightingEnabled
          ? t('courts.card.nocturna.off')
          : t('courts.card.nocturna.on')
      }
    >
      <div className="court-night-lighting-view" aria-hidden="true">
        <span className="court-night-lighting-subtitle">
          {nightLightingEnabled
            ? t('courts.card.nocturna.subtitle.on')
            : t('courts.card.nocturna.subtitle.off')}
        </span>
      </div>

      <div className="court-night-lighting-icon" aria-hidden="true">
        <FloodlightSvg active={nightLightingEnabled} />
      </div>

      <span
        className={`badge court-night-lighting-status ${
          nightLightingEnabled ? 'badge-accent' : ''
        }`}
        aria-hidden="true"
      >
        {nightLightingEnabled
          ? t('courts.card.nocturna.status.on')
          : t('courts.card.nocturna.status.off')}
      </span>
    </button>
  )
}
